import { FilterableColumn, FilterValueType, decodeFilters } from 'app/filter.utils'
import { providerFilterFetcher } from 'app/lib/fetchers/providerFilterFetcher'
import { ES_SPECIAL_CHARS } from 'lib/esSpecialChars'
import { LOCATION_RULE_COLUMN } from 'lib/filterRuleColumns'
import { PROVIDER_KEY_SEPARATOR } from 'lib/providerFilterOptions'

/**
 * Filtering the treatment board by what the treatment is.
 *
 * The board lists contacts, and the contact index knows how much unbooked
 * treatment somebody has, not what it is. The procedures index knows what it
 * is (`procedureCode`, `description`) and who it is for (`patientId`), so a
 * row here is answered there first, as a set of patients, and the band is
 * then read for those patients. The rows themselves come from the shared
 * Filters builder, encoded into the same `filters` param the location
 * drill-down uses; this module only reads out the ones it can run.
 *
 * A row means "this patient has pending treatment where …". Under AND every
 * row must hold, on the same procedure or different ones, so "code exactly
 * D2740 AND code exactly D6010" is a patient with both a crown and an
 * implant outstanding, which is what someone asking means. A negative row
 * ("not contains") means no pending line matches at all, which the index
 * cannot say per contact, so the band is read whole and cut here.
 */
export type ProcedureAttribute = `procedureCode` | `description`
export type ProcedureOperator = `contains` | `not_contains` | `exactly`
type TextRow = { attribute: ProcedureAttribute; operator: ProcedureOperator; value: string }
/** Values are provider keys, `<resourceName>@<locationId>` (see `providerFilterOptions`). */
type ProviderRow = { attribute: `providerId`; operator: `in` | `not_in`; value: string[] }
export type ProcedureRow = TextRow | ProviderRow
export type FilterJoin = `and` | `or`

const ATTRIBUTES: string[] = [`procedureCode`, `description`] satisfies ProcedureAttribute[]
const OPERATORS: string[] = [`contains`, `not_contains`, `exactly`] satisfies ProcedureOperator[]
const PROVIDER_OPERATORS: string[] = [`in`, `not_in`] satisfies ProviderRow[`operator`][]
const NEGATIVE_OPERATORS: ReadonlySet<string> = new Set([`not_contains`, `not_in`])

/**
 * What the Filters builder offers on this board. The location is declared
 * here too, `standalone`, so the builder draws the same square button the
 * contacts table has beside its own; it encodes into the same param, and
 * `readLocationIds` finds it there by attribute as before. The other rows
 * carry no `group`: a group draws a collapsible heading in the attribute
 * picker, which is furniture for a list of three.
 */
export const PROCEDURE_FILTER_COLUMNS: FilterableColumn[] = [
  { attribute: `procedureCode`, displayName: `Procedure code`, type: FilterValueType.string },
  { attribute: `description`, displayName: `Procedure description`, type: FilterValueType.string },
  { attribute: `providerId`, displayName: `Provider`, type: FilterValueType.id, fetcher: providerFilterFetcher },
  LOCATION_RULE_COLUMN,
]

/** The rows this module can run, out of everything the builder wrote to the URL. */
export function procedureRows(filtersParam: string | null): ProcedureRow[] {
  const rows: ProcedureRow[] = []
  for (const filter of decodeFilters(filtersParam)) {
    if (filter.attribute === `providerId` && filter.operator && PROVIDER_OPERATORS.includes(filter.operator)) {
      const keys = (Array.isArray(filter.value) ? filter.value : [filter.value]).filter(Boolean) as string[]
      if (keys.length)
        rows.push({ attribute: `providerId`, operator: filter.operator as ProviderRow[`operator`], value: keys })
      continue
    }
    if (!ATTRIBUTES.includes(filter.attribute) || !filter.operator || !OPERATORS.includes(filter.operator)) continue
    const value = typeof filter.value === `string` ? filter.value.trim() : ``
    if (!value) continue
    rows.push({
      attribute: filter.attribute as ProcedureAttribute,
      operator: filter.operator as ProcedureOperator,
      value,
    })
  }
  return rows
}

/**
 * The positive form of a row as a term on the procedures index. A negative
 * row is asked positively too: the patients it finds are the ones to leave
 * out. Escaped by hand rather than through the filter converter, whose
 * sanitizer strips digits, which would turn `D2740` into `D`. A wildcard
 * with a space in it never matches, so a phrase is quoted instead.
 */
export function rowSearch(row: ProcedureRow): string {
  if (row.attribute === `providerId`) {
    const terms = row.value.map((key) => {
      const [resourceName, locationId = ``] = key.split(PROVIDER_KEY_SEPARATOR)
      const provider = resourceName.replace(ES_SPECIAL_CHARS, `\\$&`)
      const location = locationId.replace(ES_SPECIAL_CHARS, `\\$&`)
      return `(providerId:"${provider}" AND locationId:"${location}")`
    })
    return `(${terms.join(` OR `)})`
  }
  const value = row.value.replace(ES_SPECIAL_CHARS, `\\$&`)
  if (row.operator === `exactly` || value.includes(` `)) return `${row.attribute}:"${value}"`
  return `${row.attribute}:*${value}*`
}

/**
 * The patients to read the band for, when every row is positive: the
 * intersection under AND, the union under OR. Insertion order is kept so the
 * chunks that follow are stable between renders.
 */
export function patientIdsToRead(rows: ProcedureRow[], sets: Set<string>[], join: FilterJoin): string[] {
  const positives = rows
    .map((row, index) => (NEGATIVE_OPERATORS.has(row.operator) ? undefined : sets[index]))
    .filter(Boolean) as Set<string>[]
  if (positives.length === 0) return []
  if (join === `or`) return Array.from(new Set(positives.flatMap((set) => Array.from(set))))
  const [first, ...rest] = positives
  return Array.from(first).filter((id) => rest.every((set) => set.has(id)))
}

/**
 * Whether one contact stays, judged from the patient sets each row found.
 * A contact may carry several patient ids (one per practice record); a row
 * holds if any of them is in its set, or for a negative row if none is.
 */
export function keepsPatient(
  patientIds: string[],
  rows: ProcedureRow[],
  sets: Set<string>[],
  join: FilterJoin,
): boolean {
  const holds = rows.map((row, index) => {
    const found = patientIds.some((id) => sets[index]?.has(id))
    return NEGATIVE_OPERATORS.has(row.operator) ? !found : found
  })
  return join === `or` ? holds.some(Boolean) : holds.every(Boolean)
}

/**
 * How many patient ids go into one `patientIds:(…)` term. Elasticsearch
 * refuses a clause past 1,024 terms, and 963 worked where 2,080 did not, so
 * this stays well under with room for the band's own terms beside it.
 */
export const PATIENT_CHUNK = 500

export function chunkPatientIds(ids: string[]): string[][] {
  const chunks: string[][] = []
  for (let start = 0; start < ids.length; start += PATIENT_CHUNK) chunks.push(ids.slice(start, start + PATIENT_CHUNK))
  return chunks
}

type Sortable = {
  lastTreatmentPlanDate?: string | null
  unbookedTreatmentValue?: number | null
}

/**
 * The order the server would have given, for a list read in pieces. Plan
 * dates are ISO strings, so they compare as strings; the unknown go last
 * whichever way the sort runs, since a blank is not a small value.
 */
export function sortContacts<T extends Sortable>(
  contacts: T[],
  sortField: `lastTreatmentPlanDate` | `unbookedTreatmentValue`,
  direction: `ASC` | `DESC`,
): T[] {
  const sign = direction === `ASC` ? 1 : -1
  return [...contacts].sort((a, b) => {
    const left = a[sortField] ?? null
    const right = b[sortField] ?? null
    if (left === null && right === null) return 0
    if (left === null) return 1
    if (right === null) return -1
    if (left === right) return 0
    return left < right ? -sign : sign
  })
}

type DrawnProcedure = { code: string; description: string; providerId?: string | null; locationId?: string | null }

const matchesRow = (procedure: DrawnProcedure, row: ProcedureRow): boolean => {
  if (row.attribute === `providerId`) {
    return row.value.includes(`${procedure.providerId}${PROVIDER_KEY_SEPARATOR}${procedure.locationId}`)
  }
  const field = (row.attribute === `procedureCode` ? procedure.code : procedure.description).toLowerCase()
  const value = row.value.toLowerCase()
  return row.operator === `exactly` ? field === value : field.includes(value)
}

/**
 * Whether a drawn card shows what the filter asked for. The index decided
 * per record, but a card draws only plan work with a fee, so a match on a
 * line the card dropped would be a card with no visible reason to be there.
 */
export function cardMatches(card: { procedures: DrawnProcedure[] }, rows: ProcedureRow[], join: FilterJoin): boolean {
  if (rows.length === 0) return true
  const holds = rows.map((row) => {
    const found = card.procedures.some((procedure) => matchesRow(procedure, row))
    return NEGATIVE_OPERATORS.has(row.operator) ? !found : found
  })
  return join === `or` ? holds.some(Boolean) : holds.every(Boolean)
}

/**
 * Rows under their own AND/OR, for when more than one set of rows must hold at once: a saved
 * treatment-plan list's rule, then whatever the Filters builder adds on top of it. Each group
 * keeps its own join, and every group must hold, so the builder can narrow a list but never
 * widen it past its rule.
 */
export type RowGroup = { rows: ProcedureRow[]; join: FilterJoin }

/**
 * The patients to read the band for across every group: each group's own set, intersected.
 * `undefined` when any group has a negative row, which no list of patients can express, so
 * the band has to be read whole.
 */
export function groupsPatientIdsToRead(groups: RowGroup[], sets: Set<string>[][]): string[] | undefined {
  if (groups.some((group) => group.rows.some((row) => NEGATIVE_OPERATORS.has(row.operator)))) return undefined
  const perGroup = groups.map((group, index) => patientIdsToRead(group.rows, sets[index], group.join))
  const [first = [], ...rest] = perGroup
  const others = rest.map((ids) => new Set(ids))
  return first.filter((id) => others.every((set) => set.has(id)))
}

/** Whether one contact stays: every group holds for it. */
export function keepsPatientInGroups(patientIds: string[], groups: RowGroup[], sets: Set<string>[][]): boolean {
  return groups.every((group, index) => keepsPatient(patientIds, group.rows, sets[index], group.join))
}

/** Whether a drawn card shows what every group asked for (see `cardMatches`). */
export function cardMatchesGroups(card: { procedures: DrawnProcedure[] }, groups: RowGroup[]): boolean {
  return groups.every((group) => cardMatches(card, group.rows, group.join))
}
